import { useEffect } from 'react'
import {
  Navigate,
  createSearchParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import { useQuery } from '@apollo/client/react'
import { Button } from 'ui'
import { useAuth } from '../lib/auth'
import { lazyComponent } from '../lib/lazyComponent'
import { isClientAccessEndedError } from '../lib/apolloClient'
import Loading from './utils/Loading'
import { SelfDocument, User } from '../operations-types'
import posthog from 'posthog-js'
import { useSupportChat } from '../hooks/useSupportChat'
import { useQueryCallbacks } from '../hooks/useQueryCallbacks'
import {
  refusedSessionReason,
  useExplainRefusedSession,
  useReportSelfUnavailable,
  useReportSignedOutRedirect,
} from '../lib/refusedSession'

const LoggedInLayout = lazyComponent(
  () => import('./layout/LoggedInLayout'),
  'default',
  'screen',
)
const FullscreenLoggedInLayout = lazyComponent(
  () => import('./FullScreenLoggedInLayout'),
  'FullscreenLoggedInLayout',
  'screen',
)

export default function LoggedIn() {
  const [_, setSearchParams] = useSearchParams()
  const self = useQuery(SelfDocument)
  const { loading, data, error } = self
  useEffect(() => LoggedInLayout.preload(), [])
  useQueryCallbacks(self, {
    onCompleted: (data) => {
      if (data.user?.user) {
        const organization = data?.user?.user?.organization
        const clientHasNoBankAccounts =
          (
            organization?.accountInfo?.bankAccounts?.filter(
              (b) => b !== null,
            ) || []
          ).length === 0
        if (clientHasNoBankAccounts && !data.user.user.passwordChangeRequired) {
          setSearchParams((params) => {
            const next = new URLSearchParams(params)
            next.set('firstTime', 'true')
            return next
          })
        }
      }
    },
  })
  const location = useLocation()

  const { signOut, isSignedIn, getAdminUserId, getAccountantUserId } = useAuth()
  const adminUserId = getAdminUserId()
  const accountantUserId = getAccountantUserId()
  const organization = data?.user?.user?.organization

  useSupportChat(!adminUserId && !loading && !!data)

  const user = data?.user
  const clientAccessEnded = isClientAccessEndedError(error)
  const mustSignOut =
    !loading &&
    !error &&
    (!user || !!user.error?.type || !user.user || !user.user.organization?.id)

  const refusedReason =
    isSignedIn() && !loading && !clientAccessEnded && !(error && !user)
      ? refusedSessionReason(user, { requireOrganization: true })
      : null
  useExplainRefusedSession(refusedReason, 'dashboard')
  useReportSignedOutRedirect(isSignedIn(), 'dashboard_signed_out')

  useEffect(() => {
    if (mustSignOut) {
      signOut('self_refused')
    }
  }, [mustSignOut, signOut])

  if (!isSignedIn()) {
    return (
      <Navigate
        to={{
          pathname: '/login',
          search: createSearchParams({
            redirect: location.pathname + location.search + location.hash,
          }).toString(),
        }}
      />
    )
  }

  if (loading || clientAccessEnded) {
    return <Loading additionalClasses={{ 'h-screen': true }} />
  }

  if (error && !user) {
    return <SelfUnavailable error={error} surface="dashboard" />
  }

  const accountant = user?.user?.organization?.accountant?.user

  if (organization) {
    posthog.group('organization', organization.id, {
      name: organization.name,
      id: organization.id,
    })
  }

  if (refusedReason || !user?.user?.organization?.id) {
    if (location) {
      return (
        <Navigate
          to={{
            pathname: '/login',
            search: createSearchParams({
              redirect: location.pathname + location.search + location.hash,
            }).toString(),
          }}
        />
      )
    }
    return <Navigate to={{ pathname: '/login' }} />
  }

  if (
    user.user.passwordChangeRequired &&
    !accountantUserId &&
    !adminUserId &&
    location.pathname !== '/dashboard/settings/security'
  ) {
    return <Navigate to="/dashboard/settings/security" />
  }
  const getName = (user?: Pick<User, 'firstName' | 'lastName'> | null) =>
    `${user?.firstName} ${user?.lastName}`
  // A HubSpot `date` property takes only YYYY-MM-DD, and rejects an empty
  // string.
  const getCreatedAtDate = (user?: Pick<User, 'createdAt'> | null) =>
    user?.createdAt
      ? new Date(user.createdAt).toISOString().slice(0, 10)
      : undefined

  posthog.register({
    acting_as: adminUserId ? 'staff' : accountantUserId ? 'accountant' : 'self',
  })
  if (!adminUserId) {
    posthog.identify(accountantUserId || user.user.id, {
      email: accountantUserId ? accountant?.email : user.user.email,
      name: accountantUserId ? getName(accountant) : getName(user.user),
      firstName: accountantUserId ? accountant?.firstName : user.user.firstName,
      lastName: accountantUserId ? accountant?.lastName : user.user.lastName,
      nickel_user_id: accountantUserId || user.user.id,
      nickel_created_at: getCreatedAtDate(
        accountantUserId ? accountant : user.user,
      ),
    })
  }

  return <LoggedInLayout loggedInUser={user.user} />
}

export function FullscreenLoggedIn() {
  const { isSignedIn } = useAuth()

  const { loading, data, error } = useQuery(SelfDocument)
  useEffect(() => FullscreenLoggedInLayout.preload(), [])
  const user = data?.user
  const clientAccessEnded = isClientAccessEndedError(error)

  const refusedReason =
    isSignedIn() && !loading && !clientAccessEnded && !(error && !user)
      ? refusedSessionReason(user, { requireOrganization: false })
      : null
  useExplainRefusedSession(refusedReason, 'fullscreen')
  useReportSignedOutRedirect(isSignedIn(), 'fullscreen_signed_out')

  if (!isSignedIn()) {
    return <Navigate to="/login" />
  }

  if (loading || clientAccessEnded) {
    return <Loading additionalClasses={{ height: '100vh' }} />
  }

  if (error && !user) {
    return <SelfUnavailable error={error} surface="fullscreen" />
  }

  if (refusedReason || !user?.user) {
    return <Navigate to="/login" />
  }

  return <FullscreenLoggedInLayout loggedInUser={user.user} />
}

function SelfUnavailable({
  error,
  surface,
}: {
  error: { message: string }
  surface: 'dashboard' | 'fullscreen'
}) {
  useReportSelfUnavailable(error, surface)
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 px-4 text-center">
      <p className="text-sm font-semibold">
        We couldn&rsquo;t load your account
      </p>
      <p className="text-sm text-muted-foreground">
        You&rsquo;re still signed in. Reload the page to try again.
      </p>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </div>
  )
}
